import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useState} from "react";
import {Extension} from "@/types/extenions.ts";
import {useCurrentSystem} from "@/hooks/useCurrentSystem.tsx";

interface ExtensionContextState {
    close: () => void;
    edit: (extension: Extension) => void;
    extension: null | Extension;
    isOpen: boolean;
    sectionsState: string[];
    setSectionsState: (sections: string[]) => void;
}

export const ExtensionContext = createContext<ExtensionContextState|null>(null);

export function ProviderExtensionEdit({children}: PropsWithChildren) {

    const [editExtension, setEditExtension] = useState<Extension|null>(null);
    const [sectionsState, setSectionsState] = useState<string[]>([]);

    const {system} = useCurrentSystem();

    useEffect(() => {
        setEditExtension(null);
    }, [system?.id]);

    const close = useCallback(() => {
        setEditExtension(null);
    }, [editExtension]);

    const edit = useCallback((extension: Extension) => {
        setEditExtension(extension);
    }, [editExtension]);

    return (
        <ExtensionContext.Provider value={{
            close,
            edit,
            extension: editExtension,
            isOpen: !!editExtension,
            sectionsState,
            setSectionsState
        }}>
            {children}
        </ExtensionContext.Provider>
    )
}

export function useExtensionEdit(extension: Extension) {
    const state = useContext(ExtensionContext);

    if(!state) {
        throw new Error('useExtensionEdit must be defined');
    }

    return {
        isEditing: state.extension?.id === extension.id,
        edit: () => state.edit(extension),
        close: state.close,
        toggle: () => extension.id === state.extension?.id ? state.close() : state.edit(extension),
    }
}

export function useExtensionEditDrawer() {
    const state = useContext(ExtensionContext);

    if(!state) {
        throw new Error('useExtensionEdit must be defined');
    }

    return state;
}
